import React, { useContext, useEffect, useState } from "react";
import { Urls } from "../../../Urls";
import axios from "../../../Axios/Axios";
import classes from "./ShowHistory.module.css";
import { DateFormatter4 } from "../../../Constants";
import { toast } from "react-toastify";
import { UserApiContext } from "../../UserDetailsContext/UserDetailsContext";
import { useInvoiceMixpanelTrack } from "../../Indent/hooks/useInvoiceMixpanelTrack";

/**
 * ShowHistory component displays a log of changes and actions based on the screen name.
 * @param {string} screen_name - name of the screen to filter the logs.
 * @returns {JSX.Element}
 */
const ShowHistory = ({ screen_name }) => {
  const [changeLogData, setChangeLogData] = useState([]);
  const [showMoreStates, setShowMoreStates] = useState({});
  const { companyProfile, userDetails } = useContext(UserApiContext);
  const [limit, setLimit] = useState(Number(10));
  const [viewMore, setViewMore] = useState(false);

  const { trackInvoiceEvent } = useInvoiceMixpanelTrack();

  /**
   * Fetches change log data.
   */
  useEffect(() => {
    document.title = "LoRRI Planning - Review Documents";
    var id = "";

    trackInvoiceEvent(
      "change log",
      "user has viewed change log screen",
      `screen name : ${screen_name}`,
    );

    if (companyProfile) {
      if (companyProfile.domain_data && companyProfile.domain_data.company_id) {
        id = companyProfile.domain_data.company_id;
        var payload = {
          skip: 0,
          limit: limit,
        };

        axios
          .post(
            `${Urls.planning}/change_log?company_id=${id}&field=recent_logs&screen=${screen_name}`,
            payload,
          )
          .then((res) => {
            setChangeLogData(res.data.logs || []);
            setViewMore(res.data.view_more);
          })
          .catch((err) => {
            console.log(err);
          });
      }
    }
  }, [companyProfile, userDetails, limit, screen_name]);

  /**
   * Toggles the visibility of additional details for a specific action.
   * @param {string} action - type of action.
   * @param {number} index - index of the item in the change log data.
   */
  const toggleShowMore = (action, index) => {
    setShowMoreStates((prevStates) => {
      const prevActionState = prevStates[action] || {};
      return {
        ...prevStates,
        [action]: {
          ...prevActionState,
          [index]: !prevActionState[index],
        },
      };
    });
  };

  /**
   * Retrieves show more state for a specific action, log or index.
   * @param {string} action - type of action.
   * @param {number} index - index of the item in the change log data.
   * @returns {boolean} True if additional details are visible, otherwise false.
   */
  const getShowMoreState = (action, index) =>
    (showMoreStates[action] && showMoreStates[action][index]) || false;

  /**
   * Handles the click event for showing more change logs.
   * If there are more logs to show, increases the limit to fetch more logs.
   * Otherwise, shows message.
   */
  const handleOnClick = () => {
    trackInvoiceEvent(
      "change log",
      "user has clicked on show more button",
      `screen name : ${screen_name}`,
    );

    if (viewMore === true) {
      setLimit(limit + 10);
    } else {
      toast.info("No more activity logs available");
    }
  };

  return (
    <>
      <div className={classes["change-log-history"]}>
        {changeLogData && changeLogData.length > 0 ? (
          changeLogData.map((data, index) => {
            return (
              <ul>
                {data.action === "Status Change" &&
                  data.screen === "review" && (
                    <li>
                      <div className="row">
                        <div className="col-8">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["change-action"]}>
                            Changed Status
                          </span>
                          {` for ${data.change_activity.length} sales order `}
                          <span
                            className={`ml-2 ${classes["view-more"]}`}
                            onClick={() => toggleShowMore(data.action, index)}
                          >
                            {getShowMoreState(data.action, index)
                              ? "view less"
                              : "view more"}
                          </span>
                        </div>
                        <div className="col-4 text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                        {getShowMoreState(data.action, index) && (
                          <ul className="col-12 mt-2">
                            {data.change_activity &&
                              data.change_activity.map((activity, idx) => (
                                <li className="col-md-6" key={idx}>
                                  {`${activity.invoice_no} to ${activity.updated_status}`}
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  )}
                {data.action === "Create Plan" && data.screen === "review" && (
                  <li>
                    <div className="row">
                      <div className="col-8">
                        {`${data.user_email.split("@")[0].toUpperCase()} `}
                        <span className={classes["create-action"]}>
                          Created Plan
                        </span>
                        {` for ${data.change_activity.length} sales order `}
                        <span
                          className={`ml-2 ${classes["view-more"]}`}
                          onClick={() => toggleShowMore(data.action, index)}
                        >
                          {getShowMoreState(data.action, index)
                            ? "view less"
                            : "view more"}
                        </span>
                      </div>
                      <div className="col-4 text-right">
                        <span className={classes["show-time"]}>
                          {DateFormatter4(data.timestamp)}
                        </span>
                      </div>
                      {getShowMoreState(data.action, index) && (
                        <ul className="col-12 mt-2">
                          {data.change_activity &&
                            data.change_activity.map((activity, idx) => (
                              <li className="col-md-6" key={idx}>
                                {`${activity.invoice_no}`}
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  </li>
                )}
                {(data.action === "File Upload" ||
                  data.action === "New File Uploaded") &&
                  (data.screen === "review" ||
                    data.screen === "freight_master" ||
                    data.screen === "location_master" ||
                    data.screen === "material_master" ||
                    data.screen === "route_master") && (
                    <li>
                      <div className="row">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["upload-action"]}>
                            Uploaded file
                          </span>
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {data.action === "Line Item Added" &&
                  (data.screen === "freight_master" ||
                    data.screen === "location_master" ||
                    data.screen === "material_master" ||
                    data.screen === "route_master") && (
                    <li>
                      <div className="row">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["added-action"]}>
                            Added Lane
                          </span>
                          {data.screen === "freight_master" ? (
                            <span>{` for ${data.change_activity.laneid}`}</span>
                          ) : data.screen === "location_master" ? (
                            <span>{` for ${data.change_activity.location_name} | Address-${data.change_activity.addressLine1} | Code-${data.change_activity.location_code}`}</span>
                          ) : data.screen === "material_master" ? (
                            <span>{` for ${data.change_activity.material_description} | Code-${data.change_activity.material_code}`}</span>
                          ) : data.screen === "route_master" ? (
                            <span>{` for ${data.change_activity.route_name} | Code-${data.change_activity.route_code}`}</span>
                          ) : data.screen === "vendor_master" ? (
                            <span>{` for ${data.change_activity.transporter_name} | Transporter ID-${data.change_activity.transporter_id}`}</span>
                          ) : null}
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {data.action === "Line Item Added" &&
                  data.screen === "vendor_master" && (
                    <li>
                      <div className="row">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["added-action"]}>
                            Added lane item
                          </span>
                          {data.screen === "vendor_master" ? (
                            <span>{` for ${data.change_activity.transporter_name} | Transporter ID-${data.change_activity.transporter_id}`}</span>
                          ) : null}
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {data.action === "Line Item Deleted" &&
                  (data.screen === "freight_master" ||
                    data.screen === "location_master" ||
                    data.screen === "material_master" ||
                    data.screen === "route_master" ||
                    data.screen === "vendor_master") && (
                    <li>
                      <div className="row">
                        <div className="col-8">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["change-action"]}>
                            Deleted
                          </span>
                          {` ${data.change_activity.length} item(s) `}
                          <span
                            className={`ml-2 ${classes["view-more"]}`}
                            onClick={() => toggleShowMore(data?.action, index)}
                          >
                            {getShowMoreState(data?.action, index)
                              ? "view less"
                              : "view more"}
                          </span>
                        </div>
                        <div className="col-4 text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                        {getShowMoreState(data.action, index) && (
                          <ul className="col-12 mt-2">
                            {data.change_activity &&
                              data.change_activity.map((activity, idx) => (
                                <li className="col-md-6" key={idx}>
                                  {data.screen === "freight_master"
                                    ? activity.laneid
                                    : data.screen === "location_master"
                                      ? `${activity.address} | Code - ${activity.location_code}`
                                      : data.screen === "material_master"
                                        ? `${activity.material_description} | Code - ${activity.material_code}`
                                        : data.screen === "route_master"
                                          ? `${activity.route_name} | Code - ${activity.route_code}`
                                          : data.screen === "vendor_master"
                                            ? `${activity.transporter_name} | Transporter ID - ${activity.transporter_id}`
                                            : null}
                                </li>
                              ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  )}
                {data.action === "Line Item Updated" &&
                  (data.screen === "freight_master" ||
                    data.screen === "location_master" ||
                    data.screen === "material_master" ||
                    data.screen === "route_master" ||
                    data.screen === "vendor_master") && (
                    <li>
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["create-action"]}>
                            Edited
                          </span>
                          {data.screen === "freight_master" ? (
                            <span>{` for ${data.change_activity.laneid}`}</span>
                          ) : data.screen === "location_master" ? (
                            <span>{` for ${data.change_activity.location_name} | code - ${data.change_activity.location_code}`}</span>
                          ) : data.screen === "material_master" ? (
                            <span>{` for Material code - ${data.change_activity.material_code}`}</span>
                          ) : data.screen === "route_master" ? (
                            <span>{` for ${data.change_activity.route_name} | Code - ${data.change_activity.route_code}`}</span>
                          ) : data.screen === "vendor_master" ? (
                            <span>
                              {` for ${data.change_activity.transporter_name}`}{" "}
                              {data.change_activity?.updated_data?.credit_term
                                ? ` | Credit term ${data.change_activity.updated_data.credit_term}`
                                : ""}{" "}
                              {data.change_activity?.updated_data?.vendor_code
                                ? ` | Vendor Code ${data.change_activity.updated_data.vendor_code}`
                                : ""}
                            </span>
                          ) : null}
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {data.action === "Line Item Updated" &&
                  data.screen === "settings" && (
                    <li>
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["create-action"]}>
                            Updated Settings
                          </span>
                          <span
                            className={`ml-2 ${classes["view-more"]}`}
                            onClick={() => toggleShowMore(data.action, index)}
                          >
                            {/* {getShowMoreState(data.action, index) ? 'view less' : 'view more'} */}
                          </span>
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                      {getShowMoreState(data.action, index) &&
                        data.change_activity?.updated_data && (
                          <div className="col-12 mt-2 ml-3">
                            {data.change_activity.existing_data && (
                              <div className="mb-2">
                                <strong>Previous Values:</strong>
                                <ul className="ml-3 mt-1">
                                  {Object.keys(
                                    data.change_activity.updated_data,
                                  ).map((key) => {
                                    const existingValue =
                                      data.change_activity.existing_data[key];
                                    return (
                                      existingValue !== undefined && (
                                        <li key={key}>
                                          <strong>{key}:</strong>{" "}
                                          {typeof existingValue === "object"
                                            ? JSON.stringify(existingValue)
                                            : String(existingValue)}
                                        </li>
                                      )
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                            <div>
                              <strong>Updated Values:</strong>
                              <ul className="ml-3 mt-1">
                                {Object.entries(
                                  data.change_activity.updated_data,
                                ).map(([key, value]) => (
                                  <li key={key}>
                                    <strong>{key}:</strong>{" "}
                                    {typeof value === "object"
                                      ? JSON.stringify(value)
                                      : String(value)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                    </li>
                  )}
                {(data.action === "Line Item Updated" ||
                  data.action === "Line Item Added" ||
                  data.action === "Line Item Deleted") &&
                  data.screen === "teams" && (
                    <li>
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()} `}
                          <span className={classes["create-action"]}>
                            Updated Teams/Organizations
                          </span>
                          <span
                            className={`ml-2 ${classes["view-more"]}`}
                            onClick={() => toggleShowMore(data.action, index)}
                          >
                            {getShowMoreState(data.action, index)
                              ? "view less"
                              : "view more"}
                          </span>
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                      {getShowMoreState(data.action, index) &&
                        data.change_activity && (
                          <div className="col-12 mt-2 ml-3">
                            {data.change_activity.existing_data && (
                              <div className="mb-2">
                                {data.change_activity?.existing_data
                                  ?.contact_information &&
                                  Object.keys(
                                    data.change_activity.existing_data
                                      .contact_information,
                                  ).length == 0 &&
                                  data.change_activity?.updated_data
                                    ?.contact_information &&
                                  Object.keys(
                                    data.change_activity.updated_data
                                      .contact_information,
                                  ).length > 0 && (
                                    <div className="mb-2">
                                      <strong>Users Added:</strong>
                                      <ul className="ml-3">
                                        {Object.keys(
                                          data.change_activity.updated_data
                                            .contact_information,
                                        ).map((email) => (
                                          <li key={email}>{email}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                {data.change_activity?.existing_data
                                  ?.contact_information &&
                                  data.change_activity?.updated_data
                                    ?.contact_information &&
                                  Object.keys(
                                    data.change_activity.existing_data
                                      .contact_information,
                                  ).length > 0 &&
                                  Object.keys(
                                    data.change_activity.updated_data
                                      .contact_information,
                                  ).length > 0 && (
                                    <div className="mb-2">
                                      <strong>Access Control Updated:</strong>

                                      {Object.keys(
                                        data.change_activity.updated_data
                                          .contact_information,
                                      ).map(
                                        (email) =>
                                          data.change_activity.existing_data
                                            .contact_information[email] && (
                                            <div
                                              key={email}
                                              className="ml-3 mt-2"
                                            >
                                              <strong>{email}:</strong>

                                              <ul className="ml-3">
                                                {data.change_activity.updated_data.contact_information[
                                                  email
                                                ]?.access_control?.modules
                                                  ?.filter((updatedMod) => {
                                                    const existingMod =
                                                      data.change_activity.existing_data.contact_information[
                                                        email
                                                      ]?.access_control?.modules?.find(
                                                        (m) =>
                                                          m.module_key ===
                                                            updatedMod.module_key &&
                                                          m.screen ===
                                                            updatedMod.screen,
                                                      );

                                                    // if module didn't exist before, treat it as changed (newly added)
                                                    if (!existingMod)
                                                      return true;

                                                    // show only if access changed
                                                    return (
                                                      existingMod.can_access !==
                                                        updatedMod.can_access ||
                                                      existingMod.no_access !==
                                                        updatedMod.no_access ||
                                                      existingMod.can_access_reports !==
                                                        updatedMod.can_access_reports ||
                                                      existingMod.dashboard_access !==
                                                        updatedMod.dashboard_access
                                                    );
                                                  })
                                                  .map((updatedMod) => {
                                                    const existingMod =
                                                      data.change_activity.existing_data.contact_information[
                                                        email
                                                      ]?.access_control?.modules?.find(
                                                        (m) =>
                                                          m.module_key ===
                                                            updatedMod.module_key &&
                                                          m.screen ===
                                                            updatedMod.screen,
                                                      );

                                                    if (!existingMod)
                                                      return null;

                                                    return (
                                                      <li
                                                        key={`${updatedMod.module_key}-${updatedMod.screen}`}
                                                      >
                                                        <strong>
                                                          {
                                                            updatedMod.module_key
                                                          }
                                                        </strong>
                                                        <ul className="ml-3">
                                                          {(existingMod.can_access !==
                                                            updatedMod.can_access ||
                                                            existingMod.no_access !==
                                                              updatedMod.no_access) && (
                                                            <li>
                                                              Access:{" "}
                                                              {existingMod.no_access
                                                                ? "Not Allowed"
                                                                : existingMod.can_access
                                                                  ? "Allowed"
                                                                  : "View Only"}{" "}
                                                              →{" "}
                                                              {updatedMod.no_access
                                                                ? "Not Allowed"
                                                                : updatedMod.can_access
                                                                  ? "Allowed"
                                                                  : "View Only"}
                                                            </li>
                                                          )}
                                                          {existingMod.can_access_reports !==
                                                            updatedMod.can_access_reports && (
                                                            <li>
                                                              reports:{" "}
                                                              {existingMod.can_access_reports
                                                                ? "Allowed"
                                                                : "Not allowed"}{" "}
                                                              →{" "}
                                                              {updatedMod.can_access_reports
                                                                ? "Allowed"
                                                                : "Not allowed"}
                                                            </li>
                                                          )}

                                                          {existingMod.dashboard_access !==
                                                            updatedMod.dashboard_access && (
                                                            <li>
                                                              dashboard:{" "}
                                                              {existingMod.dashboard_access
                                                                ? "Allowed"
                                                                : "Not Allowed"}{" "}
                                                              →{" "}
                                                              {updatedMod.dashboard_access
                                                                ? "Allowed"
                                                                : "Not Allowed"}
                                                            </li>
                                                          )}
                                                        </ul>
                                                      </li>
                                                    );
                                                  })}
                                              </ul>
                                            </div>
                                          ),
                                      )}
                                    </div>
                                  )}

                                {data.change_activity?.existing_data
                                  ?.contact_information &&
                                  data.change_activity?.updated_data
                                    ?.contact_information &&
                                  Object.keys(
                                    data.change_activity.existing_data
                                      .contact_information,
                                  ).length > 0 &&
                                  Object.keys(
                                    data.change_activity.updated_data
                                      .contact_information,
                                  ).length > 0 && (
                                    <>
                                      {/* Deleted Users */}
                                      {Object.keys(
                                        data.change_activity.existing_data
                                          .contact_information,
                                      )
                                        .filter(
                                          (email) =>
                                            !data.change_activity.updated_data
                                              .contact_information[email],
                                        )
                                        .map((email) => (
                                          <div
                                            key={`deleted-${email}`}
                                            className="mb-2"
                                          >
                                            <strong>User Deleted:</strong>{" "}
                                            {email}
                                          </div>
                                        ))}

                                      {/* Added Users */}
                                      {Object.keys(
                                        data.change_activity.updated_data
                                          .contact_information,
                                      )
                                        .filter(
                                          (email) =>
                                            !data.change_activity.existing_data
                                              .contact_information[email],
                                        )
                                        .map((email) => (
                                          <div
                                            key={`added-${email}`}
                                            className="mb-2"
                                          >
                                            <strong>User Added:</strong> {email}
                                          </div>
                                        ))}
                                    </>
                                  )}
                                {data.change_activity.updated_data
                                  ?.organizations &&
                                  data.change_activity.existing_data
                                    .organizations && (
                                    <div className="ml-3 mt-1">
                                      <strong>Organizations:</strong>
                                      <ul className="ml-3">
                                        {Object.entries(
                                          data.change_activity.existing_data
                                            .organizations,
                                        )
                                          .filter(
                                            ([orgId]) =>
                                              data.change_activity.updated_data
                                                .organizations[orgId],
                                          )
                                          .map(([orgId, orgData]) => (
                                            <li key={orgId}>
                                              <strong>{orgId}:</strong>{" "}
                                              {JSON.stringify(orgData)}
                                            </li>
                                          ))}
                                      </ul>
                                    </div>
                                  )}
                              </div>
                            )}
                            {data.change_activity.updated_data && (
                              <div>
                                {data.change_activity?.existing_data
                                  ?.contact_information &&
                                  Object.keys(
                                    data.change_activity.existing_data
                                      .contact_information,
                                  ).length > 0 &&
                                  data.change_activity?.updated_data
                                    ?.contact_information &&
                                  Object.keys(
                                    data.change_activity.updated_data
                                      .contact_information,
                                  ).length === 0 && (
                                    <div className="mb-2">
                                      <strong>Users Deleted:</strong>
                                      <ul className="ml-3">
                                        {Object.keys(
                                          data.change_activity.existing_data
                                            .contact_information,
                                        ).map((email) => (
                                          <li key={email}>{email}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                {data.change_activity.updated_data
                                  .organizations && (
                                  <div className="ml-3 mt-1">
                                    <strong>Organizations:</strong>
                                    <ul className="ml-3">
                                      {Object.entries(
                                        data.change_activity.updated_data
                                          .organizations,
                                      ).map(([orgId, orgData]) => (
                                        <li key={orgId}>
                                          <strong>{orgId}:</strong>{" "}
                                          {JSON.stringify(orgData)}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                    </li>
                  )}
                {data.action === "Accept" && data.screen === "invoice" && (
                  <li className="invoice">
                    <div className="d-flex">
                      <div className="col-md-9">
                        {`${data && data.user_email ? data.user_email.split("@")[0].toUpperCase() : ""} (${data && data.team ? data.team[0].toUpperCase() + data.team.slice(1) : ""}) `}
                        <span className={classes["create-action"]}>
                          Accepted
                        </span>
                        <span>{` Invoice with Invoice ID ${data.change_activity.invoice_id} from ${data.change_activity.origin} to ${data.change_activity.destination} ${data.team === "accounts" ? `and Posting Document Number ${data.change_activity.posting_doc_no}` : ""}`}</span>
                      </div>
                      <div className="col text-right">
                        <span className={classes["show-time"]}>
                          {DateFormatter4(data.timestamp)}
                        </span>
                      </div>
                    </div>
                  </li>
                )}
                {(data.action === "On Hold" ||
                  data.action === "Moved to Open") &&
                  data.screen === "invoice" && (
                    <li className="invoice">
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()}  (${data.team[0].toUpperCase() + data.team.slice(1)}) `}
                          {data.action === "On Hold" ? (
                            <span className={classes["upload-action"]}>
                              On Hold
                            </span>
                          ) : (
                            <span className={classes["move-to-open"]}>
                              Moved to Open
                            </span>
                          )}
                          <span>{` Invoice with Invoice ID ${data.change_activity.invoice_id} from ${data.change_activity.transporter_name}}`}</span>
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {(data.action === "Approve" || data.action === "Reject") &&
                  data.screen === "invoice" && (
                    <li className="invoice">
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()}  (${
                            data.team[0].toUpperCase() + data.team.slice(1)
                          }) `}
                          {data.action === "Approve" ? (
                            <span className={classes["create-action"]}>
                              Approved
                            </span>
                          ) : (
                            <span className={classes["change-action"]}>
                              Rejected
                            </span>
                          )}
                          <span>{` Invoice with Invoice ID ${data.change_activity.invoice_id} from ${data.change_activity.transporter_name}`}</span>
                          {data.remarks && <span>{` as ${data.remarks}`}</span>}
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {data.action === "Bulk Accept" && data.screen === "invoice" && (
                  <li className="invoice">
                    <div className="d-flex">
                      <div className="col-md-9">
                        {`${data.user_email.split("@")[0].toUpperCase()}  (${data.team[0].toUpperCase() + data.team.slice(1)}) `}
                        <span className={classes["create-action"]}>
                          Bulk Accepted
                        </span>
                        <span> Invoices with </span>
                        {data &&
                        data.change_activity &&
                        data.change_activity.changeLogData &&
                        data.change_activity.changeLogData.length > 0
                          ? data.change_activity.changeLogData.map((item) => {
                              return (
                                <span>{`Invoice ID ${item.invoice_id} for ${item.transporter_name}, `}</span>
                              );
                            })
                          : null}
                      </div>
                      <div className="col text-right">
                        <span className={classes["show-time"]}>
                          {DateFormatter4(data.timestamp)}
                        </span>
                      </div>
                    </div>
                  </li>
                )}
                {data.action === "Clarification" &&
                  data.screen === "invoice" && (
                    <li className="invoice">
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()}  (${data.team[0].toUpperCase() + data.team.slice(1)})`}
                          <span> has Raised</span>
                          <span className={classes["change-action"]}>
                            Clarification
                          </span>
                          <span>{` For Invoice ID ${data.change_activity.invoice_id} for ${data?.change_activity?.transporter_name} as ${data.remarks}`}</span>
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {(data.action === "clarification-sent-to-account" ||
                  data.action === "clarification-sent-to-transporter") &&
                  data.screen === "invoice" && (
                    <li className="invoice">
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()}  (${data.team[0].toUpperCase() + data.team.slice(1)}) `}
                          <span> has sent</span>
                          <span
                            className={classes["change-action"]}
                          >{`${data.action === "clarification-sent-to-transporter" ? "Clarification for" : "Clarification remark for"}`}</span>
                          {data.change_activity.origin ? (
                            <span>{` Invoice ID ${data?.change_activity?.invoice_id} for ${data?.change_activity?.transporter_name}`}</span>
                          ) : (
                            <span>{` Invoice ID ${data.change_activity.invoice_id} from ${data.change_activity.origin} to ${data.change_activity.destination}`}</span>
                          )}
                          {data.action ===
                          "clarification-sent-to-transporter" ? (
                            <span> To Transporter</span>
                          ) : (
                            <span>{` to Accounts Team as ${data.remarks}`}</span>
                          )}
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {(data.action === "Damages" || data.action === "Shortages") &&
                  data.screen === "invoice" && (
                    <li className="invoice">
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()}  (${data.team[0].toUpperCase() + data.team.slice(1)})`}
                          <span> has Updated</span>
                          <span
                            className={classes["change-action"]}
                          >{`${data.action === "Damages" ? " Damages for " : " Shortages for "}`}</span>
                          <span>{` Invoice ID ${data.change_activity.invoice_id} as `}</span>
                          {data.action === "Damages" &&
                            data.change_activity.consignmentDetails &&
                            data.change_activity.consignmentDetails.length >
                              0 &&
                            data.change_activity.consignmentDetails.map(
                              (item) =>
                                item?.orders_list.map((order, index) => (
                                  <>
                                    {" "}
                                    <br />
                                    <span key={index}>
                                      {` Document Number:  ${item.invoice_no} , Damage Quantity: ${order.damage_quantity}, Damage Value: ${order.damage_amount} for Material Code ${order.material_code} and Material Description ${order.material_name}`}{" "}
                                    </span>
                                  </>
                                )),
                            )}
                          {data.action === "Shortages" &&
                            data.change_activity.consignmentDetails &&
                            data.change_activity.consignmentDetails.length >
                              0 &&
                            data.change_activity.consignmentDetails.map(
                              (item) =>
                                item?.orders_list.map((order, index) => (
                                  <>
                                    {" "}
                                    <br />
                                    <span key={index}>
                                      {`Document Number: ${item.invoice_no}, Shortage Quantity: ${order.shortage_quantity}, Shortage Value: ${order.shortage_amount} for Material Code ${order.material_code} and Material Description ${order.material_name}`}
                                    </span>
                                  </>
                                )),
                            )}
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
                {data.action === "item-level-clarification" &&
                  data.screen === "invoice" && (
                    <li className="invoice">
                      <div className="d-flex">
                        <div className="col-md-9">
                          {`${data.user_email.split("@")[0].toUpperCase()}  (${data.team[0].toUpperCase() + data.team.slice(1)}) has Updated Remarks of `}
                          {data.change_activity.name ===
                          "Loading & Unloading" ? (
                            <span className={classes["move-to-open"]}>
                              Loading & Unloading
                            </span>
                          ) : data.change_activity.name === "Detention" ? (
                            <span className={classes["move-to-open"]}>
                              Detention
                            </span>
                          ) : (
                            <span className={classes["move-to-open"]}>
                              {data.change_activity.name}
                            </span>
                          )}
                          <span>{` for Invoice with Invoice ID ${data.change_activity.invoice_id} for ${data.change_activity.transporter_name} as ${data.change_activity.remark}`}</span>
                        </div>
                        <div className="col text-right">
                          <span className={classes["show-time"]}>
                            {DateFormatter4(data.timestamp)}
                          </span>
                        </div>
                      </div>
                    </li>
                  )}
              </ul>
            );
          })
        ) : (
          <div>No changes found</div>
        )}
      </div>
      <div className="text-center mt-2">
        <button className="btn btn-light" onClick={handleOnClick}>
          Show More
        </button>
      </div>
    </>
  );
};

export default ShowHistory;
